import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { deleteItems, updateStateData } from "../../../Store/actions";
import { fetchCampaignNames, fetchOwnerNames, fetchRetailerRoots, loadCampaigns } from "../../../api_calls/campaignRequests";
import { Box } from "@mui/material";
import ActionButton from "../../../components/commonComponents/Buttons/ActionButton";
import { useNavigate } from "react-router-dom";
import { checkUserDetailsFromCookie, checkUserPermissions, checkUserRole } from "../../../api_calls/userManagement";
import { createCookie, deleteCookie } from "../../../api_calls/requestConfig";
import { LISTING_RELOAD } from "../../../api_calls/listingReload";
import useListingOptionsVersion from "../../../hooks/useListingOptionsVersion";

import FilterPanel from "../../../components/commonComponents/Filters/FilterPanel";

const CampaignListFilters = () => {

  const Campaigns = useSelector((state) => state.userSelections);
  const verifyUserRoleName = checkUserRole();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  // Dropdown lists reload after this table finishes a fetch.
  const optionsVersion = useListingOptionsVersion(
    Campaigns,
    LISTING_RELOAD.campaigns,
    Campaigns.campaign_list
  );

  // Same user types that see the Retailer listing column
  const checkUserDetails = checkUserDetailsFromCookie();
  const userType =
    checkUserDetails.success === true ? checkUserDetails.user_detail.user_type : "";
  const canUseRetailerFilter = ["cyreen", "tenant"].includes(userType);

  // Define filter configuration. loadOptions runs when that dropdown opens,
  // not when the pane opens. silent keeps the page backdrop off; the open
  // list shows its own spinner. Progress and Shared are fixed lists.
  const campaignFiltersConfig = React.useMemo(() => [
    {
      key: 'campaign_name',
      label: 'Campaign Name',
      type: 'multi',
      loadOptions: () => fetchCampaignNames({ silent: true }),
      options: () => Campaigns?.campaign_names_info?.campaign_name_obj,
    },
    {
      key: 'progress',
      label: 'Progress',
      disableSearch: true,
      type: 'multi',
      selectionParam: "label",
      options: () => [
        { value: 1, label: 'Active' },
        { value: 2, label: 'Scheduled' },
        { value: 3, label: 'Completed' },
      ],
    },
    {
      key: 'owner',
      label: 'Owner',
      type: 'multi',
      loadOptions: () => fetchOwnerNames("list-campaigns", { silent: true }),
      options: () => Campaigns?.companies_info,
    },
    {
      // Drawer key "retailer"; Redux/cookie key is root_retailer_filter
      key: 'retailer',
      label: 'Retailer',
      type: 'multi',
      loadOptions: () => fetchRetailerRoots({ silent: true }),
      options: () => Campaigns?.retailer_roots_info,
      enabled: canUseRetailerFilter,
    },
    {
      key: 'shared',
      label: 'Shared',
      type: 'single',
      disableSearch: true,
      selectionParam: "label",
      options: () => [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ],
      enabled: permissionScope.campaign_can_share,
    },
  ], [
    Campaigns?.campaign_names_info,
    Campaigns?.companies_info,
    Campaigns?.retailer_roots_info,
    canUseRetailerFilter,
    permissionScope.campaign_can_share,
  ]);

  const navigate = useNavigate();

  const dispatchAction = useDispatch();

  const saveCampaignFilters = (selectedFilters) => {
    const filterValues = {
      // Multi-select drawer filters
      campaign_name: selectedFilters.campaign_name || [],
      // Drawer UI key is "owner"; Redux/cookie key is owner_filter (label stays "Owner")
      owner_filter: selectedFilters.owner || [],
      // Drawer UI key is "retailer"; Redux/cookie key is root_retailer_filter
      root_retailer_filter: selectedFilters.retailer || [],
      // Map progress drawer filter to duration_filter
      duration_filter: selectedFilters.progress || [],
      // Single-select drawer filter converted to array; empty array if no value
      shared_filter: selectedFilters.shared ? [selectedFilters.shared] : [],
    };

    // If no filters are selected anywhere, clear everything
    const noFiltersSelected =
      (!filterValues.campaign_name || filterValues.campaign_name.length === 0) &&
      (!filterValues.owner_filter || filterValues.owner_filter.length === 0) &&
      (!filterValues.root_retailer_filter || filterValues.root_retailer_filter.length === 0) &&
      (!filterValues.assigned_to_filter || filterValues.assigned_to_filter.length === 0) &&
      (!filterValues.status_filter || filterValues.status_filter.length === 0) &&
      (!filterValues.startdate_filter || filterValues.startdate_filter.length === 0) &&
      (!filterValues.enddate_filter || filterValues.enddate_filter.length === 0) &&
      (!filterValues.duration_filter || filterValues.duration_filter.length === 0) &&
      (!filterValues.shared_filter || filterValues.shared_filter.length === 0);
    if (noFiltersSelected) {
      // If nothing is selected, clear existing filters and return
      clearfilters();
      return;
    }
    // Persist filter values to Redux (for PowerPoint export, etc.)
    dispatchAction(
      updateStateData({
        campaign_list_filters: filterValues,
        campaign_list_filter_applied: true,
      })
    );
    // Save filters to cookies for persistence across sessions
    createCookie("campaign_list_filters", JSON.stringify(filterValues));
    // Reload campaigns with filters applied (reset to page 1 but keep page size)
    loadCampaigns(0, Campaigns.campaign_pagination.pageSize);
  };

  const clearfilters = () => {
    // delete filters cookie
    deleteCookie("campaign_list_filters", true);
    // reset campaign listing
    dispatchAction(deleteItems(["campaign_list_filters", "filtered_campaign_list", "campaign_list_filter_applied"]));
    // fetch campaigns again with filters applied
    loadCampaigns(0, Campaigns.campaign_pagination.pageSize);
  };

  // Map Redux state filters to FilterPanel expected keys
  const getMappedInitialFilters = () => {
    if (!Campaigns.campaign_list_filters) {
      return {};
    }

    return {
      campaign_name: Campaigns.campaign_list_filters.campaign_name || [],
      progress: Campaigns.campaign_list_filters.duration_filter || [],
      // Map Redux owner_filter back to the FilterPanel "owner" drawer key
      owner: Campaigns.campaign_list_filters.owner_filter || [],
      // Map Redux root_retailer_filter back to the FilterPanel "retailer" drawer key
      retailer: Campaigns.campaign_list_filters.root_retailer_filter || [],
      shared: Campaigns.campaign_list_filters.shared_filter?.[0] || null,
    };
  };

  const overviewButton = permissionScope.campaign_dashboard_overview_can_view === true && verifyUserRoleName.user_role !== "Brand" ? (
    <ActionButton
      onClick={() => {
        navigate("/check-campaigns/overview");
      }}
      label={"Overview"}
    />
  ) : null;

  return (
    <Box>
      <FilterPanel
        tableId="campaign_list"
        optionsVersion={optionsVersion}
        filtersConfig={campaignFiltersConfig}
        applyFilterFunc={saveCampaignFilters}
        clearFilterFunc={clearfilters}
        initiallyAppliedVals={getMappedInitialFilters()}
        rightAction={overviewButton}
      />
    </Box>
  );
};

export default CampaignListFilters;
